/**
 * useVisualViewport — Phase 2a (vibe-designer 1.124.0).
 *
 * Side-effect hook (returns void) that listens to window.visualViewport
 * resize + scroll events and writes the keyboard-induced viewport shrink
 * as `--keyboard-offset` on `document.documentElement.style`.
 *
 * Consumed by the Pi chat input footer: its wrapper sets
 * `paddingBottom: var(--keyboard-offset, 0px)` (ChatPanelPi.tsx) so the in-flow
 * footer lifts above the iOS soft keyboard. (Before 2026-07-26 this var fed the
 * old absolutely-positioned queue row's `bottom: calc(...)`; that row + its
 * `--pi-message-editor-height` ResizeObserver chase were removed when the input
 * moved in-flow.)
 *
 * Feature-detect: `window.visualViewport` is undefined on older browsers
 * (Safari < 13, Chrome < 61 — pre-2018). Graceful no-op; the footer's
 * `var(--keyboard-offset, 0px)` fallback resolves to 0px (no shift), so the
 * desktop layout is unchanged.
 *
 * The offset is the difference between the layout viewport height
 * (window.innerHeight) and the visual viewport height + scroll offset.
 * When the keyboard opens, visualViewport.height shrinks; offset > 0.
 * When the keyboard closes, visualViewport.height = innerHeight; offset = 0.
 *
 * Math.max(0, offset) defense-in-depth — iOS Safari occasionally reports
 * transient negative offsets during pinch-zoom; clamping to 0 prevents the
 * queue row from jumping above its baseline.
 *
 * Mount once at the app shell level (App.tsx VibeDesignerApp) so the
 * listener persists across viewMode toggles and route navigations.
 */
import { useEffect } from 'react';

export function useVisualViewport(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }

    const update = () => {
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      const clamped = Math.max(0, offset);
      document.documentElement.style.setProperty(
        '--keyboard-offset',
        `${clamped}px`,
      );
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      document.documentElement.style.removeProperty('--keyboard-offset');
    };
  }, []);
}
